import {useState, useRef, useEffect} from 'react';
import styled from 'styled-components';
import type {TicketPriority} from '../types/ticket';

interface Props {
	value: TicketPriority;
	onChange: (priority: TicketPriority) => void;
	disabled: boolean;
}

const OPTIONS: {value: TicketPriority; label: string; dotColor: string}[] = [
	{value: 'p0', label: 'Critical', dotColor: '#ef4444'},
	{value: 'p1', label: 'High', dotColor: '#fb923c'},
	{value: 'p2', label: 'Medium', dotColor: '#facc15'},
	{value: 'p3', label: 'Low', dotColor: '#94a3b8'},
];

const Container = styled.div`
	position: relative;
`;
const Trigger = styled.button`
	display: inline-flex;
	width: 100%;
	align-items: center;
	gap: 6px;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 4px 8px;
	text-align: left;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #1e293b;
	cursor: pointer;
	&:disabled {
		opacity: 0.5;
	}
`;
const Dot = styled.span<{$color: string}>`
	width: 8px;
	height: 8px;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: ${p => p.$color};
`;
const OptionList = styled.ul`
	position: absolute;
	left: 0;
	top: 100%;
	z-index: 50;
	margin: 4px 0 0;
	width: 100%;
	min-width: 160px;
	list-style: none;
	padding: 4px 0;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	box-shadow: ${p => p.theme.ticketing.shadow.shadow2};
`;
const Option = styled.li<{$selected: boolean}>`
	display: flex;
	cursor: pointer;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: ${p => (p.$selected ? 500 : 400)};
	color: ${p => (p.$selected ? '#1d4ed8' : '#1e293b')};
	background: ${p => (p.$selected ? '#eff6ff' : 'transparent')};
	&:hover {
		background: ${p => (p.$selected ? '#eff6ff' : '#f8fafc')};
	}
`;
const UrgentTag = styled.span`
	margin-left: auto;
	font: ${p => p.theme.ticketing.font.body3};
	color: #ef4444;
`;

export function PriorityPicker({value, onChange, disabled}: Props) {
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const current = OPTIONS.find(o => o.value === value)!;

	useEffect(() => {
		if (!open) return;
		function handleClickOutside(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [open]);

	function handleSelect(priority: TicketPriority) {
		onChange(priority);
		setOpen(false);
	}

	return (
		<Container ref={containerRef}>
			<Trigger
				type="button"
				aria-label="Priority"
				aria-haspopup="listbox"
				aria-expanded={open}
				disabled={disabled}
				onClick={() => !disabled && setOpen(o => !o)}
			>
				<Dot $color={current.dotColor} aria-hidden="true" />
				<span>{current.label}</span>
			</Trigger>

			{open && (
				<OptionList role="listbox" aria-label="Priority options">
					{OPTIONS.map(option => (
						<Option
							key={option.value}
							role="option"
							aria-label={option.label}
							aria-selected={option.value === value}
							$selected={option.value === value}
							onClick={() => handleSelect(option.value)}
						>
							<Dot $color={option.dotColor} aria-hidden="true" />
							<span>{option.label}</span>
							{option.value === 'p0' && <UrgentTag>Urgent</UrgentTag>}
						</Option>
					))}
				</OptionList>
			)}
		</Container>
	);
}
